import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pie } from '../../themed/charts';

import { INTERNAL_USER_GUIDE_URL, tutorialConstants } from '../../../utils/constants';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';

import { InstructionItem, RiskLevel, User } from '../../../utils/models';
import { formatTime, matchPhrases, getCriticalityFromRating } from '../../../utils/utilities';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import AwaitingActivation from '../../themed/awaiting-activation';
import Conditional from '../../common/conditional';
import DashboardLoader from './dashboardLoader';
import DashboardSearchBar from '../../blocks/dashboard-seacrhbar';
import DashboardOverviewItem from './dashboardOverviewItem';
import DBEmptyData from './dashboardEmptyData';
import Instruction from '../../themed/instruction-modals/instruction';
import PDFDownload from '../../themed/pdf-download';
import RecentProjects from '../../blocks/recent-projects';
import Signature from '../../blocks/signature';
import SyncTime from '../../blocks/sync-time';
import WelcomeToExternalUser from '../../themed/instruction-modals/welcomeToExternalUser';

import useInternalDashboard from '../../../hooks/useInternalDashboard';
import withProjects, { ProjectProps } from '../../hoc/withProjects';

import classNames from './dashboard.module.scss';
import config from '../../../utils/config';
import routes from '../../../utils/routes';

import * as authService from '../../../services/authService';
import * as dummyData from '../../../utils/data';

import file from '../../../assets/animations/file.json';
import notificationBell from '../../../assets/animations/notifications.json';
import pdf from '../../../assets/animations/pdf.json';
import success from '../../../assets/animations/success.json';

const InternalUserDashboard: React.FC<ProjectProps> = () => {
	const [privileges, setPrivileges] = useState<InstructionItem[]>([]);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const tutorials = useAppSelector(getTutorials);
	const internalDashboard = useInternalDashboard();

	const ratings: number[] = useMemo(() => {
		return internalDashboard.overview.riskRating
			.filter((r) => r !== null)
			.map((r: { riskRating: number }) => r.riskRating)
			.filter((r) => r !== null);
	}, [internalDashboard.overview.riskRating]);

	const getInspectionChartBreakdown = () => {
		const types = internalDashboard.overview.projectsByInspectionType;
		if (types.length === 0) return null;

		const labels = types.map((b: any) => b.inspectionTypeName);
		const data = types.map((b: any) => b.count);
		const backgroundColor = dummyData.getInspectionChartColors();

		return {
			labels,
			datasets: [{ label: 'Inpsection Type Breakdown', data, backgroundColor }],
		};
	};

	const handleViewNextTutorial = () => {
		if (user.role.manageUsers) {
			dispatch(setActiveTutorial(tutorialConstants.MANAGE_USERS));
			navigate(routes.users.fullPath);
		} else {
			dispatch(setActiveTutorial(tutorialConstants.ALL_PROJECTS));
			navigate(routes.projects.fullPath);
		}
	};

	const initializePrivileges = () => {
		const newPrivileges: InstructionItem[] = [];

		if (user.role.manageProjects) {
			newPrivileges.push(allPrivileges.CREATE_PROJECT);
		}

		if (user.role.manageReports) {
			newPrivileges.push(allPrivileges.MANAGE_REPORTS);
		}

		if (user.role.reviewReports || user.role.approveReports) {
			newPrivileges.push(allPrivileges.ASSESS_REPORTS);
		}

		if (user.role.manageUsers) {
			newPrivileges.push(allPrivileges.MANAGE_USERS);
		}

		if (newPrivileges.length < 4) {
			newPrivileges.push(allPrivileges.VIEW_PROJECTS);
		}

		setPrivileges(newPrivileges);
	};

	const riskBreakdown = useMemo(() => {
		if (ratings.length === 0) return null;

		const riskLevels: RiskLevel[] = dummyData.getRiskLevels();
		const labels: string[] = riskLevels.map((r) => r.level);
		const backgroundColor: string[] = riskLevels.map((r) => r.backgroundColor);

		const criticalities = ratings.map((r) => getCriticalityFromRating(r));
		const data = riskLevels.map((r) => criticalities.filter((c) => matchPhrases(c, r.level)).length);

		return {
			labels,
			datasets: [{ label: 'Risk Rating Breakdown', data, backgroundColor }],
		};
	}, [ratings]);

	const user = authService.getCurrentUser() as User;
	const inspectionBreakdown = getInspectionChartBreakdown();

	useEffect(() => {
		initializePrivileges();

		// eslint-disable-next-line
	}, []);

	return (
		<div className={classNames.internalUserDashboard}>
			<AwaitingActivation />

			<WelcomeToExternalUser instructionItems={privileges} />

			<Instruction
				isVisible={tutorials.activeTutorial === tutorialConstants.INTERNAL_DASHBOARD}
				onProceed={handleViewNextTutorial}
				title="Welcome to your dashboard!"
				message="This is where the numbers are! Total number of reports, recent reports, breakdown by inspection type and much more!"
			/>

			<div className="justify-content-between align-items-center d-flex">
				<h1 className={classNames.internalUserDashboardTitle}>
					Welcome, <span>{user.firstName}</span>
				</h1>

				<div className="d-flex align-items-center">
					<div className="mx-4">
						<PDFDownload
							documentLabel="User Guide"
							documentSource={INTERNAL_USER_GUIDE_URL}
						/>
					</div>

					<div>
						<DashboardSearchBar />
					</div>

					<div className={classNames.internalUserDashboardUserDetails}>
						<div className={classNames.userInfo}>
							<div className={classNames.userInfoTitle}>
								{user.firstName} {user.lastName}
							</div>
							<div className={classNames.userInfoSubtitle}>
								Last Login: {formatTime(user.lastLogin as string)}
							</div>
						</div>

						<div className={classNames.userIcon}>
							<i className="bi bi-person-lines-fill"></i>
						</div>
					</div>
				</div>
			</div>

			<div className="mt-4">
				<SyncTime lastSynced={internalDashboard.lastUpdated as number} />
			</div>

			<DashboardLoader isVisible={internalDashboard.loading} />

			<Conditional isVisible={!internalDashboard.loading}>
				<div className={classNames.overview}>
					<DashboardOverviewItem
						label="total projects"
						value={internalDashboard.overview.totalProjectsInDivision}
						iconColor="#7a869a"
						icon="kanban-fill"
					/>

					<DashboardOverviewItem
						label="active projects"
						value={internalDashboard.overview.totalActiveProjectsInDivision}
						iconColor="#ab3c3c"
						icon="activity"
					/>

					<DashboardOverviewItem
						label="inactive projects"
						value={internalDashboard.overview.totalInActiveProjectsInDivision}
						iconColor="#04223b"
						icon="alarm-fill"
					/>

					<DashboardOverviewItem
						label="completed projects"
						value={internalDashboard.overview.completedProjects}
						icon="check-circle-fill"
						iconColor="#11ab45"
					/>
				</div>

				<div className={classNames.block}>
					<div className={classNames.blockItem}>
						<div className={classNames.blockItemLeft}>
							<h3>
								Report Overview <span>Breakdown</span>
							</h3>

							<div className={classNames.blockItemLeftOverview}>
								<div className={classNames.blockItemLeftOverviewValue}>
									{internalDashboard.overview.totalReportsGenerated}
								</div>

								<div className={classNames.blockItemLeftOverviewTitle}>
									Total Generated Reports
								</div>
							</div>

							<div className={classNames.blockItemLeftOverview}>
								<div className={classNames.blockItemLeftOverviewValue}>
									{internalDashboard.overview.pendingApprovalReports}
								</div>
								<div className={classNames.blockItemLeftOverviewTitle}>
									Pending Approval
								</div>
							</div>

							<div className={classNames.blockItemLeftOverview}>
								<div className={classNames.blockItemLeftOverviewValue}>
									{internalDashboard.overview.approvedReports}
								</div>
								<div className={classNames.blockItemLeftOverviewTitle}>
									Approved Reports
								</div>
							</div>

							<div className={classNames.blockItemLeftOverview}>
								<div className={classNames.blockItemLeftOverviewValue}>
									{internalDashboard.overview.rejectedReports}
								</div>
								<div className={classNames.blockItemLeftOverviewTitle}>
									Rejected Reports
								</div>
							</div>

							<div className={classNames.blockItemLeftOverview}>
								<div className={classNames.blockItemLeftOverviewValue}>
									{internalDashboard.overview.acceptedReports}
								</div>
								<div className={classNames.blockItemLeftOverviewTitle}>
									Accepted Reports
								</div>
							</div>
						</div>
					</div>

					<div className={classNames.blockItem}>
						<div className={classNames.blockItemChart}>
							<div>
								<h3 className="text-left">
									Report Breakdown <span>By Inspection Type</span>
								</h3>

								<DBEmptyData
									isVisible={!inspectionBreakdown}
									message="No inspections have been created yet"
								/>

								<Conditional isVisible={!!inspectionBreakdown}>
									<Pie chartData={inspectionBreakdown} />
								</Conditional>
							</div>
						</div>
					</div>
				</div>

				<div className={classNames.overview}>
					<DashboardOverviewItem
						label="total internal users"
						value={internalDashboard.overview.totalInternalUsers}
						iconColor="#11ab45"
						icon="person-bounding-box"
					/>

					<DashboardOverviewItem
						label="total contact persons"
						value={internalDashboard.overview.totalExternalUsers}
						iconColor="#7a869a"
						icon="people-fill"
					/>

					<DashboardOverviewItem
						label="total organizations"
						value={internalDashboard.overview.totalOrganisations}
						iconColor="#ab3c3c"
						icon="building"
					/>

					<DashboardOverviewItem
						label="total facilities"
						value={internalDashboard.overview.totalFacilities}
						iconColor="#04223b"
						icon="pc"
					/>
				</div>

				<div className={classNames.block}>
					<Signature />

					<Conditional isVisible={config.enableRiskRating}>
						<div className={classNames.blockItem}>
							<div className={classNames.blockItemChart}>
								<div>
									<h3 className="text-left">
										Risk Rating <span>Breakdown</span>
									</h3>

									<DBEmptyData
										isVisible={!riskBreakdown}
										message="No risk breakdown is available yet"
									/>

									<Conditional isVisible={!!riskBreakdown}>
										<Pie chartData={riskBreakdown} />
									</Conditional>
								</div>
							</div>
						</div>
					</Conditional>

					<RecentProjects recentProjects={internalDashboard.overview.recentProjects} />
				</div>
			</Conditional>
		</div>
	);
};

const allPrivileges = {
	CREATE_PROJECT: {
		title: 'Create Projects!',
		message: 'You can create projects on the go!',
		animation: pdf,
	},
	MANAGE_REPORTS: {
		title: 'Generate Reports!',
		message: "You can generate reports on projects you've been assigned to!",
		animation: file,
	},
	ASSESS_REPORTS: {
		title: 'Assess Generated Reports!',
		message: 'Give feedback on completed reports.',
		animation: success,
	},
	MANAGE_USERS: {
		title: 'Manage Users!',
		message:
			'You are the IT guy! You can setup user accounts, clients, assign privileges etc..',
		animation: notificationBell,
	},
	VIEW_PROJECTS: {
		title: 'View Projects',
		message: "You can view projects you've been tagged in",
		animation: pdf,
	},
};

export default withProjects(InternalUserDashboard);
